import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  isDevMode,
  signal,
  viewChild,
} from '@angular/core';
import { DeclarativeMapBase } from '../declarative-map-base/declarative-map-base.component';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import { explicitEffect } from 'ngxtension/explicit-effect';

@Component({
  selector: 'cue-declarative-map-canvas',
  imports: [],
  templateUrl: './declarative-map-canvas.component.html',
  styleUrl: './declarative-map-canvas.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeclarativeMapCanvasComponent extends DeclarativeMapBase implements AfterViewInit {
  elRef = inject(ElementRef);
  /**
   * Invlaidate map on element resize
   */
  invalidateOnResize = input<boolean, boolean | 'true' | 'false'>(false, {
    transform: booleanAttribute,
  });

  bindResizeObserver = explicitEffect([this.invalidateOnResize], ([invalidate]) => {
    if (!invalidate) {
      return undefined;
    }
    const resizeObserver = new ResizeObserver(() => {
      this.adapter.invalidateMapSize();
    });
    resizeObserver.observe(this.elRef.nativeElement);
    return () => {
      resizeObserver.disconnect();
    };
  });

  zoom = input<number>(1);
  debug = input<boolean>(false);
  minZoom = input<number>(0.5);
  maxZoom = input<number>(3);
  resetZoom = input<number>(1);

  showCurrentLocationButton = input<boolean>(false);
  setLocationButton = explicitEffect([this.showCurrentLocationButton], ([show]) => {
    this.adapter.showCurrentLocationButton(show);
  });

  currentLocationClicked = outputFromObservable(this.adapter.currentLocationClicked);

  zoomChanged = outputFromObservable(
    this.adapter.zoomChanged.pipe(
      tap((x) => {
        this.innerZoom.set(x);
      })
    )
  );

  innerZoom = signal(0);

  zoomOutsideIn = explicitEffect([this.zoom], ([zoom]) => {
    if (this.innerZoom() === zoom) {
      return;
    }
    this.innerZoom.set(zoom);
    this.adapter.setZoom(this.identifier, zoom);
  });

  zoomInsideOut = explicitEffect([this.innerZoom], ([innerZoom]) => {
    if (innerZoom === this.zoom()) {
      return;
    }
    this.adapter.setZoom(this.identifier, this.zoom());
  });

  affectMinZoom = effect(() => {
    this.adapter.setMinZoom(this.identifier, this.minZoom());
  });

  affectMaxZoom = effect(() => {
    this.adapter.setMaxZoom(this.identifier, this.maxZoom());
  });

  affectResetZoom = effect(() => {
    this.adapter.setResetZoom(this.identifier, this.resetZoom());
  });

  ngAfterViewInit() {
    if (this.debug()) {
      this.adapter.enableDebug();
    }
    this.adapter.initializeCanvas(this.identifier, this.elRef.nativeElement, {
      zoom: this.zoom(),
      minZoom: this.minZoom(),
      maxZoom: this.maxZoom(),
    });
  }

  protected readonly isDevMode = isDevMode;
}
